import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Autocomplete, Box, Button, Chip, Divider, ListSubheader, Radio, RadioGroup,
    FormControlLabel, TextField, Tooltip, Typography
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";

import Langs, { Localize } from "../../app/lang/langs";
import { vlearnInputSx, vlearnSurface, vlearnType } from "./vlearnUi";
import {
    getVLearnAudience, getVLearnAudienceTargets, refreshVLearnAudience, saveVLearnAudience
} from "../../containers/vlearn/vlearnSlice";

/**
 * V-Learning — who has to take this course.
 *
 * ── The picker only ever offers what the server will accept ──────────────────────────
 *
 * `getAudienceTargets` answers with the teams this author may assign to and the present
 * members of those teams, and nothing else. `saveAudience` then intersects the request
 * against the same list and REFUSES — 403, not a quietly narrowed audience — if anything
 * falls outside it. So the two refusals it can return ("a team you are not allowed to assign
 * courses to", "a collaborator outside the teams you manage") should be unreachable from
 * this screen. They are still shown verbatim if they arrive, because seeing one means the
 * author's permissions changed while this tab was open, and quietly swallowing that would
 * leave them convinced they had assigned a course they had not.
 *
 * ── Why the count changes after saving ───────────────────────────────────────────────
 *
 * The figure above the Save button is an ESTIMATE, computed from the targetable collab list
 * held in the store. The server's `resolveEnrollments` answer — `{added, deactivated, total}`
 * — is the truth: it re-reads team membership at that instant, skips anyone who has left,
 * reactivates people who rejoined, and deactivates those no longer in scope. That is why the
 * estimate is replaced by the server's numbers as soon as they arrive rather than being
 * reconciled with them.
 */

/** See the identical note in CourseEditor: two thunk families, two error shapes. */
const serverMessage = (payload) => {
    const error = payload && payload.responseError
    if (!error) return null
    const data = error.data || (error.response && error.response.data)
    return (data && data.error) || null
}

const collabLabel = (collab) =>
    `${collab.prenom || ''} ${collab.nom || ''}`.trim() || collab.email || `#${collab.id}`

export default function AudiencePicker({ idCourse }) {

    const dispatch = useDispatch()

    const audience = useSelector((state) => state.vlearn.audience)
    // Truthiness of `audience` is not enough: it is a populated default object even before
    // anything has been fetched. See the note in the slice.
    const audienceLoaded = useSelector((state) => state.vlearn.audienceLoaded)
    const targets = useSelector((state) => state.vlearn.audienceTargets)
    const lang = useSelector((state) => state.settings.lang)

    const [targetType, setTargetType] = useState('teams')
    const [teams, setTeams] = useState([])
    const [collabs, setCollabs] = useState([])
    const [saving, setSaving] = useState(false)
    const [refusal, setRefusal] = useState(null)
    /** The server's own `{added, deactivated, total}`, once it has answered. */
    const [result, setResult] = useState(null)

    /**
     * The course whose stored audience has already been copied into local state.
     *
     * Without it every arriving `getVLearnAudience` — including the one a save triggers —
     * would overwrite a selection the author is in the middle of building.
     */
    const hydrated = useRef(null)

    const requested = useRef(null)
    useEffect(() => {
        if (!idCourse || requested.current === idCourse) return
        requested.current = idCourse
        // What this author MAY target, and what is currently set. Two calls because the first
        // answer is about the author and the second about the course.
        dispatch(getVLearnAudienceTargets({}))
        dispatch(getVLearnAudience({ id_course: idCourse }))
    }, [dispatch, idCourse])

    // The stored rule holds ids; the pickers hold the full option objects, so the two lists
    // are joined once both responses are in.
    useEffect(() => {
        // `audienceLoaded`, not `audience`: hydrating from the placeholder and then latching
        // is what made a "Mixed" course reopen as "By team".
        if (!audienceLoaded || !audience || !targets || hydrated.current === idCourse) return
        if (!(targets.teams || []).length && !(targets.collabs || []).length) return
        hydrated.current = idCourse
        // A stored 'all' that this author may no longer choose falls back to 'teams' rather
        // than leaving the radio group with nothing selected: the permission was narrowed
        // after the rule was written, and an unselected group reads as a bug.
        const stored = audience.target_type || 'teams'
        setTargetType(stored === 'all' && !targets.canTargetAll ? 'teams' : stored)
        const teamIds = (audience.teams || []).map(t => Number(t.id_team))
        const collabIds = (audience.collabs || []).map(c => Number(c.id_collab))
        setTeams((targets.teams || []).filter(t => teamIds.includes(Number(t.id))))
        setCollabs((targets.collabs || []).filter(c => collabIds.includes(Number(c.id))))
    }, [audienceLoaded, audience, targets, idCourse])

    const showTeams = targetType === 'teams' || targetType === 'mixed'
    const showCollabs = targetType === 'collabs' || targetType === 'mixed'

    /**
     * The estimate.
     *
     * A union, not a sum: someone who is both a member of a selected team and named
     * individually is one person, and adding the two counts would overstate the audience of
     * every mixed course.
     */
    const reachEstimate = useMemo(() => {
        if (targetType === 'all') return null
        const ids = new Set()
        if (showTeams) {
            const teamIds = teams.map(t => Number(t.id))
            for (const collab of (targets.collabs || [])) {
                if ((collab.team_ids || []).some(id => teamIds.includes(Number(id)))) {
                    ids.add(Number(collab.id))
                }
            }
        }
        if (showCollabs) collabs.forEach(collab => ids.add(Number(collab.id)))
        return ids.size
    }, [targetType, showTeams, showCollabs, teams, collabs, targets])

    /** The team a collaborator is grouped under: the first of their teams, alphabetically. */
    const groupOf = useCallback((collab) => {
        const names = String(collab.team_names || '').split(',').map(s => s.trim()).filter(Boolean)
        return names[0] || Localize("VLEARN_NO_TEAM", lang)
    }, [lang])

    /**
     * Adds every collaborator of one team group in a single click.
     *
     * This is the case the screen exists for: an author responsible for four teams builds ONE
     * course for named people across all of them, which `saveAudience` explicitly allows —
     * the collabs a caller may name are the members of the teams they manage, not the members
     * of the teams named on this course. Without this control that author would tick
     * eighty boxes or, more likely, create four near-identical courses.
     */
    const selectAllInGroup = (group) => {
        const additions = (targets.collabs || []).filter(collab => groupOf(collab) === group)
        setCollabs(prev => {
            const seen = new Set(prev.map(c => Number(c.id)))
            return [...prev, ...additions.filter(c => !seen.has(Number(c.id)))]
        })
    }

    const onSave = async () => {
        setRefusal(null)
        setSaving(true)
        const response = await dispatch(saveVLearnAudience({
            id_course: idCourse,
            target_type: targetType,
            team_ids: showTeams ? teams.map(t => t.id) : [],
            collab_ids: showCollabs ? collabs.map(c => c.id) : []
        }))
        setSaving(false)
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            setResult(data.enrolled || null)
            toast.success(Localize("VLEARN_AUDIENCE_SAVED", lang))
            // Re-read so `enrolledCount` and the stored rule match what was just written.
            dispatch(getVLearnAudience({ id_course: idCourse }))
            return
        }
        // Verbatim. See the file comment: one of these means something is out of sync, and
        // the author is the only person who can act on that.
        const message = serverMessage(response.payload)
        setRefusal(message || Localize("VLEARN_ACTION_FAILED", lang))
        toast.error(message || Localize("VLEARN_ACTION_FAILED", lang))
    }

    const onRefresh = async () => {
        const response = await dispatch(refreshVLearnAudience({ id_course: idCourse }))
        const data = response.payload && response.payload.responseData
        if (data && data.status === 1) {
            setResult(data.enrolled || null)
            toast.success(Localize("VLEARN_AUDIENCE_REFRESHED", lang))
            dispatch(getVLearnAudience({ id_course: idCourse }))
        } else {
            toast.error(serverMessage(response.payload) || Localize("VLEARN_ACTION_FAILED", lang))
        }
    }

    // 16px below `sm`: iOS Safari zooms the whole page when a focused input's
    // computed font-size is under 16px, and it does not zoom back out.
    const inputSx = vlearnInputSx

    const option = (labelKey, helpKey, value, disabledReasonKey) => {
        const control = (
            <FormControlLabel
                value={value}
                disabled={!!disabledReasonKey}
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: 'var(--orange, rgb(255,144,21))' } }} />}
                label={
                    <Box sx={{ py: 0.5 }}>
                        <Typography sx={{ fontSize: '0.88rem', fontWeight: 600 }}>
                            <Langs str={labelKey} />
                        </Typography>
                        <Typography sx={vlearnType.meta}><Langs str={helpKey} /></Typography>
                    </Box>
                }
                sx={{ alignItems: 'flex-start', mr: 3 }}
            />
        )
        if (!disabledReasonKey) return control
        return (
            <Tooltip arrow title={Localize(disabledReasonKey, lang)}>
                <span>{control}</span>
            </Tooltip>
        )
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* ── The rule ───────────────────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                <Typography component="h2" sx={vlearnType.sectionTitle}>
                    <Langs str="VLEARN_AUDIENCE_TITLE" />
                </Typography>
                <Typography sx={{ ...vlearnType.body, mt: 0.5, mb: 2, maxWidth: 640 }}>
                    <Langs str="VLEARN_AUDIENCE_INTRO" />
                </Typography>

                <RadioGroup
                    value={targetType}
                    onChange={(event) => setTargetType(event.target.value)}
                    sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}
                >
                    {/* "All teams" is only drawn for a holder of vlearn edit-all, because
                        that is exactly who `saveAudience` accepts it from. A team-scoped
                        author has to name their teams. */}
                    {targets.canTargetAll
                        ? option('VLEARN_TARGET_ALL', 'VLEARN_TARGET_ALL_HELP', 'all')
                        : null}
                    {option('VLEARN_TARGET_TEAMS', 'VLEARN_TARGET_TEAMS_HELP', 'teams')}
                    {option('VLEARN_TARGET_COLLABS', 'VLEARN_TARGET_COLLABS_HELP', 'collabs')}
                    {option('VLEARN_TARGET_MIXED', 'VLEARN_TARGET_MIXED_HELP', 'mixed')}
                </RadioGroup>
            </Box>

            {/* ── Teams ──────────────────────────────────────────────────── */}
            {showTeams ? (
                <Box sx={vlearnSurface.panel}>
                    <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 0.5 }}>
                        <Langs str="VLEARN_PICK_TEAMS" />
                    </Typography>
                    <Typography sx={{ ...vlearnType.body, mb: 1.5 }}>
                        <Langs str="VLEARN_PICK_TEAMS_HELP" />
                    </Typography>

                    <Autocomplete
                        multiple disableCloseOnSelect
                        options={targets.teams || []}
                        value={teams}
                        onChange={(event, value) => setTeams(value)}
                        getOptionLabel={(team) => team.name || ''}
                        isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                        renderOption={(props, team) => (
                            <li {...props} key={team.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <GroupsOutlinedIcon sx={{ fontSize: 17, color: 'var(--orange, rgb(255,144,21))' }} />
                                    <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
                                        <Typography sx={{ fontSize: '0.85rem' }}>{team.name}</Typography>
                                        {team.client_name ? (
                                            <Typography sx={vlearnType.meta}>{team.client_name}</Typography>
                                        ) : null}
                                    </Box>
                                    {/* The member count is the only thing that turns "three
                                        teams" into a size the author can judge. */}
                                    <Typography sx={vlearnType.meta}>
                                        <Langs
                                            str={Number(team.members_count) === 1
                                                ? 'VLEARN_MEMBER_COUNT_ONE' : 'VLEARN_MEMBER_COUNT_MANY'}
                                            params={{ count: Number(team.members_count) || 0 }}
                                        />
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderTags={(value, getTagProps) => value.map((team, index) => (
                            <Chip size="small" {...getTagProps({ index })} key={team.id}
                                label={`${team.name} · ${Number(team.members_count) || 0}`}
                                sx={{ fontSize: '0.75rem' }} />
                        ))}
                        renderInput={(params) => (
                            <TextField {...params} size="small" sx={inputSx}
                                label={<Langs str="VLEARN_TEAMS" />}
                                placeholder={Localize("VLEARN_PICK_TEAMS_PLACEHOLDER", lang)} />
                        )}
                    />
                </Box>
            ) : null}

            {/* ── Collaborators ──────────────────────────────────────────── */}
            {showCollabs ? (
                <Box sx={vlearnSurface.panel}>
                    <Typography component="h2" sx={{ ...vlearnType.sectionTitle, mb: 0.5 }}>
                        <Langs str="VLEARN_PICK_COLLABS" />
                    </Typography>
                    <Typography sx={{ ...vlearnType.body, mb: 1.5, maxWidth: 640 }}>
                        <Langs str="VLEARN_PICK_COLLABS_HELP" />
                    </Typography>

                    <Autocomplete
                        multiple disableCloseOnSelect
                        options={[...(targets.collabs || [])].sort(
                            // Sorted by group first: MUI renders `groupBy` in the order the
                            // options arrive, so an unsorted list produces the same team
                            // heading several times.
                            (a, b) => groupOf(a).localeCompare(groupOf(b))
                                || collabLabel(a).localeCompare(collabLabel(b)))}
                        value={collabs}
                        onChange={(event, value) => setCollabs(value)}
                        groupBy={groupOf}
                        getOptionLabel={collabLabel}
                        isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                        renderGroup={(params) => (
                            <li key={params.key}>
                                <ListSubheader sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    lineHeight: '32px', backgroundColor: '#fff'
                                }}>
                                    <Box component="span" sx={{
                                        fontSize: '0.75rem', fontWeight: 700,
                                        textTransform: 'uppercase', letterSpacing: '.04em',
                                        flex: '1 1 auto', minWidth: 0
                                    }}>
                                        {params.group}
                                    </Box>
                                    {/* onMouseDown, not onClick: the Autocomplete closes its
                                        popup on the blur a click would cause, and the author
                                        is usually about to pick from another team next. */}
                                    <Button size="small"
                                        onMouseDown={(event) => {
                                            event.preventDefault()
                                            event.stopPropagation()
                                            selectAllInGroup(params.group)
                                        }}
                                        sx={{ textTransform: 'none', fontSize: '0.72rem' }}>
                                        <Langs str="VLEARN_SELECT_ALL_IN_TEAM" />
                                    </Button>
                                </ListSubheader>
                                <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                    {params.children}
                                </Box>
                            </li>
                        )}
                        renderOption={(props, collab) => (
                            <li {...props} key={collab.id}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    <PersonOutlineIcon sx={{ fontSize: 17, color: '#8b92a0' }} />
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.85rem' }}>
                                            {collabLabel(collab)}
                                        </Typography>
                                        <Typography sx={vlearnType.meta}>
                                            {collab.team_names || collab.email}
                                        </Typography>
                                    </Box>
                                </Box>
                            </li>
                        )}
                        renderTags={(value, getTagProps) => value.map((collab, index) => (
                            <Chip size="small" {...getTagProps({ index })} key={collab.id}
                                label={collabLabel(collab)} sx={{ fontSize: '0.75rem' }} />
                        ))}
                        renderInput={(params) => (
                            <TextField {...params} size="small" sx={inputSx}
                                label={<Langs str="VLEARN_COLLABS" />}
                                placeholder={Localize("VLEARN_PICK_COLLABS_PLACEHOLDER", lang)} />
                        )}
                    />
                </Box>
            ) : null}

            {/* ── Reach, save and refresh ────────────────────────────────── */}
            <Box sx={vlearnSurface.panel}>
                {refusal ? (
                    <Alert severity="error" sx={{ mb: 2, fontSize: '0.82rem' }}>{refusal}</Alert>
                ) : null}

                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 2,
                    flexWrap: 'wrap', justifyContent: 'space-between'
                }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700 }}>
                            {targetType === 'all'
                                ? <Langs str="VLEARN_REACH_ALL" />
                                : <Langs
                                    str={reachEstimate === 1
                                        ? 'VLEARN_REACH_ESTIMATE_ONE' : 'VLEARN_REACH_ESTIMATE_MANY'}
                                    params={{ count: reachEstimate }} />}
                        </Typography>
                        <Typography sx={vlearnType.meta}>
                            <Langs str="VLEARN_REACH_ESTIMATE_HELP" />
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" onClick={onSave} disabled={saving}
                            sx={{
                                textTransform: 'none',
                                backgroundColor: 'var(--orange, rgb(255,144,21))',
                                '&:hover': { backgroundColor: '#e07f0d' }
                            }}>
                            <Langs str="VLEARN_SAVE_AUDIENCE" />
                        </Button>
                        <Tooltip arrow title={Localize("VLEARN_REFRESH_AUDIENCE_TIP", lang)}>
                            <Button variant="outlined" onClick={onRefresh}
                                startIcon={<RefreshIcon sx={{ fontSize: 17 }} />}
                                sx={{ textTransform: 'none' }}>
                                <Langs str="VLEARN_REFRESH_AUDIENCE" />
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>

                <Typography sx={{ ...vlearnType.meta, mt: 1 }}>
                    <Langs str="VLEARN_REFRESH_AUDIENCE_HELP" />
                </Typography>

                {result ? (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography component="h3" sx={{ ...vlearnType.sectionTitle, mb: 0.5 }}>
                            <Langs str="VLEARN_ENROLLED_RESULT_TITLE" />
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" sx={{ fontSize: '0.75rem' }}
                                label={<Langs str="VLEARN_ENROLLED_TOTAL"
                                    params={{ count: result.total }} />} />
                            <Chip size="small" sx={{ fontSize: '0.75rem' }}
                                label={<Langs str="VLEARN_ENROLLED_ADDED"
                                    params={{ count: result.added }} />} />
                            <Chip size="small" sx={{ fontSize: '0.75rem' }}
                                label={<Langs str="VLEARN_ENROLLED_DEACTIVATED"
                                    params={{ count: result.deactivated }} />} />
                        </Box>
                        <Typography sx={{ ...vlearnType.meta, mt: 1 }}>
                            <Langs str="VLEARN_ENROLLED_RESULT_HELP" />
                        </Typography>
                    </>
                ) : (
                    <Typography sx={{ ...vlearnType.meta, mt: 1.5 }}>
                        <Langs str="VLEARN_CURRENTLY_ENROLLED"
                            params={{ count: (audience && audience.enrolledCount) || 0 }} />
                    </Typography>
                )}
            </Box>
        </Box>
    )
}
